<template>
    <div class="m-cart">
        <y-loading
            :active="$wait.is('loading-cart-page')"
            height="100vh"
        >
            <y-stepper
                v-if="myCart && myCart.length"
                ref="stepper"
                :steps="steps"
                :lang="$i18n.locale"
                no-footer
            >
                <template #stepper-slot-1>
                    <y-step-wrapper
                        :invoice-model="invoiceModel"
                        :description="$t('cart.invoice.description')"
                        :disable-next="!!Boolean(['loading-change-count-item', 'loading-remove-item'].find(item => $wait.is(item)))"
                        @next="next"
                    >
                        <y-order-list
                            :my-cart="get(myCart, '0.items', [])"
                            @openModal="openModal($event)"
                            @updateOrderList="addItems(productsType, $event, false, 'loading-change-count-item')"
                        />
                    </y-step-wrapper>
                </template>

                <template #stepper-slot-2>
                    <y-step-wrapper
                        v-show="emptyAddressList !== 'empty'"
                        :invoice-model="invoiceModel"
                        :description="$t('cart.invoice.description')"
                        :next-hint="$t('cart.address_list.choose_address')"
                        :disable-next="!(model.address && model.shipping_method)"
                        @next="next"
                    >
                        <y-profile-addresses
                            ref="addressList"
                            type="simple"
                            :title="$t('cart.address_list.title')"
                            raised
                            selectable
                            @selectedAddress="fetchShippingMethods($event, myCart[0].id)"
                            @emptyAddressList="emptyAddressList = 'empty'"
                        />

                        <y-shipping-list
                            :disabled="!shippingMethods"
                            :shipping-list="shippingMethods"
                            @selectedShipping="shippingHandler"
                        />

                        <y-delivery-intervals
                            :disabled="!freeSlots"
                            :delivery-intervals="freeSlots"
                            @selectedIntervals="intervalsHandler"
                        />

                        <div class="electronic-factor">
                            <span class="info-background">
                                <y-icon
                                    icon="info"
                                    color="star"
                                    size="tiny"
                                    inner
                                />
                            </span>
                            <p class="text">
                                {{ $t('cart.electronic_factor') }}
                            </p>
                        </div>
                    </y-step-wrapper>
                    <y-empty
                        v-if="emptyAddressList === 'empty'"
                        reverse
                        :title="$t('cart.address_list.empty.title')"
                        :text="$t('cart.address_list.empty.text')"
                        :src="require('@modules/cart/assets/images/empty-address.svg')"
                    >
                        <y-button
                            icon-prepend="plus"
                            color="primary"
                            class="mt-10"
                            flat
                            block
                            :content="$t('cart.address_list.add_address')"
                            @click.prevent="addAddress"
                        />
                    </y-empty>
                </template>

                <template #stepper-slot-3>
                    <y-step-wrapper
                        :invoice-model="invoiceModel"
                        :description="$t('cart.invoice.description')"
                        :next-hint="$t('cart.payment_path.choose_path')"
                        :button-content="$t('cart.buttons.payment')"
                        :disable-next="!letFinalize"
                        @next="finalizeOrder"
                    >
                        <y-payment-path
                            :disabled="!paymentPathOnline"
                            :payment-list="paymentPathOnline"
                            :wallet="get(wallets, '0')"
                            @selectedPayment="paymentHandler"
                        />

                        <y-coupons
                            :order-list-id="myCart[0].id"
                            :selected-address="get(model, 'address')"
                            @coupons="couponsHandler"
                        />

                        <y-cart-review
                            :my-cart="get(myCart, '0.items', [])"
                            @edit="doEditCart"
                        />
                    </y-step-wrapper>
                </template>
            </y-stepper>

            <y-container v-else>
                <y-empty
                    :title="$t('cart.empty.title')"
                    :src="require('@/assets/images/empty.svg')"
                />
            </y-container>
        </y-loading>
        <y-modal
            ref="removeModal"
            :title="$t('cart.delete.modal_title')"
            loading-on="loading-remove-item"
            :next-content="$t('cart.delete.cancel')"
            :back-content="$t('cart.delete.remove')"
            @back="removeItem(removeWare)"
            @next="closeModal"
        >
            <template #body>
                <h6 class="typo-body-1 color-body-text mt-0 mb-5">
                    {{ $t('cart.delete.title') }}
                </h6>
            </template>
        </y-modal>
    </div>
</template>

<script>
    import debounce from '@nodes/helpers/api';

    // Mixins
    import PageMixin from '@nodes/mixins/Page';
    import CartMixin from '@nodes/mixins/Cart';

    // components
    import {
        YModal, YEmpty, YStepper, YIcon,
    } from '@components';
    import { YProfileAddresses } from '@rogues';
    import {
        YCoupons,
        YOrderList,
        YCartReview,
        YPaymentPath,
        YStepWrapper,
        YShippingList,
        YDeliveryIntervals,
    } from '@/modules/cart/blocks';

    export default {

        name: 'Cart',

        components: {
            YIcon,
            YModal,
            YEmpty,
            YStepper,
            YCoupons,
            YOrderList,
            YCartReview,
            YProfileAddresses,
            YPaymentPath,
            YStepWrapper,
            YShippingList,
            YDeliveryIntervals,
        },

        mixins: [PageMixin, CartMixin],

        /**
         * @inheritDoc
         */
        data() {
            return {
                removeWare      : null,
                letFinalize     : false,
                emptyAddressList: null,
                freeSlots       : null,
                wallets         : null,

                steps: [
                    {
                        icon : 'cart-plus',
                        title: this.$t('cart.stepper.shopping_basket'),
                    },
                    {
                        icon : 'truck',
                        title: this.$t('cart.stepper.shipping'),
                    },
                    {
                        icon : 'check',
                        title: this.$t('cart.stepper.payment'),
                    },
                ],
            };
        },

        /**
         * @inheritDoc
         */
        created() {
            this.debounceFetchPaymentPath = debounce(() => {
                this.fetchPaymentPath(this.myCart[0].id);
            }, 500);
        },

        methods: {
            /**
             * Do edit cart
             */
            doEditCart() {
                this.$refs.stepper.currentStep = 1;
                this.model.shipping_method = null;
                this.model.shipping_slot = null;
                this.model.address = null;
                this.model.path_id = null;
                this.freeSlots = null;
                this.invoiceModel.cost_shipping = undefined;
                this.letFinalize = false;
                this.shippingMethods = null;
            },

            /**
             * Open Address Editor Modal
             */
            addAddress() {
                const { divisionConfig } = this.$refs.addressList.$children[0]._data;
                this.$refs.addressList.$children[0].$refs.addressEditor.openModal(null, divisionConfig);
                this.emptyAddressList = null;
            },

            /**
             * Open modal.
             *
             * @param {object} item - select item
             */
            openModal(item) {
                this.removeWare = item;
                this.$refs.removeModal.open();
            },

            /**
             * Close modal.
             */
            closeModal() {
                this.$refs.removeModal.close();
            },

            /**
             * Handler Shipping object, that emitted.
             * 
             * @param {object} obj - emitted object
             */
            shippingHandler(obj) {
                if (obj) {
                    if (!this.model.shipping_method) {
                        this.debounceFetchPaymentPath();
                        this.fetchUserWallets();
                    }
                    this.model.shipping_method = null;
                    this.model.shipping_method = obj.id;
                    this.freeSlots = obj.free_slots ? obj.free_slots : [];
                    this.invoiceModel.cost_shipping = obj.freight;
                } else {
                    this.model.shipping_method = null;
                    this.freeSlots = null;
                    this.invoiceModel.cost_shipping = 0;
                    this.paymentPathOnline = null;
                }
            },

            /**
             * Handler coupons object, that emitted.
             * 
             * @param {object} obj - emitted obj
             */
            couponsHandler(obj) {
                if (obj) {
                    this.coupons = [obj.coupon_id];
                    this.invoiceModel.discount += obj.total_discount;
                } else {
                    this.coupons = null;
                }
            },

            /**
             * Handler intervals value, that emitted.
             * 
             * @param {string} value - emitted value
             */
            intervalsHandler(value) {
                if (value) {
                    this.model.shipping_slot = value;
                } else {
                    this.model.shipping_slot = null;
                }
            },

            /**
             * Handler payment object, that emitted.
             * 
             * @param {object} obj - emitted obj
             */
            paymentHandler(obj) {
                if (obj) {
                    this.model.path_id = obj.id;
                    this.letFinalize = true;
                } else {
                    this.model.path_id = null;
                    this.letFinalize = false;
                }
            },

            /**
             * Custom Remove Item
             */
            afterSuccessRemoveItem() {
                this.closeModal();
            },

            /**
             * Custom Finalize Order.
             */
            afterSuccessFinalizeOrder() {
                this.paymentTransaction(this.$config('cart.repayment_model_key'));
            },
        
            /**
             * Custom Payment Transaction.
             */
            afterSuccessPaymentTransaction() {
                this.letFinalize = false;
                this.model = {};
            },

            /**
             * Check user status before go to next step
             */
            next() {
                if (!this.$store.getters['auth/isLoggedIn']) {
                    this.$store.dispatch('auth/setRedirect', {
                        name  : this.$route.name,
                        params: { ...this.$route.params },
                        query : { ...this.$route.query },
                    });
                    this.$toast.info(this.$t('cart.login_first'));
                    this.$router.push({ name: 'auth-login' });
                } else {
                    this.$refs.stepper.nextClick();
                }
            },

            /**
             * Fetch User Wallets
             *
             * @returns {*}
             */
            fetchUserWallets() {
                this.$wait.start('fetch-wallets');
                return this.$services.Wallet.myWallets().then((response) => {
                    this.wallets = response.data.results;
                }).catch((error) => {
                    this.handleError(error);
                }).finally(() => {
                    this.$wait.end('fetch-wallets');
                });
            },
        },
    };
</script>
